import type { ForwardedRef, InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useState } from 'react';

import { cn } from '~/chords/lib/cn';
import { variants } from '~/chords/lib/variants';

import type { ComponentProps } from '../ComponentProps';
import { getClassNameOverride } from '../getClassNameOverride';
import { Tag } from '../Tag';

interface RadioGroupOption {
  value: string;
  label: ReactNode;
}

type RadioGroupVariant = 'tag' | 'radio';
type RadioGroupSpacing = 'sm' | 'md' | 'lg' | 'xl';

const radioGroup = variants({
  slots: {
    fieldset: cn('flex', 'flex-wrap'),
    wrapper: cn('inline-flex items-center gap-sm'),
    input: '',
  },
  variants: {
    variant: {
      tag: {
        input: cn('sr-only'),
      },
      radio: {
        input: cn(
          'relative h-md w-md cursor-pointer appearance-none rounded-full border-px border-strong',
          "after:absolute after:inset-[2px] after:scale-0 after:rounded-full after:transition-transform after:content-['']",
          'checked:after:scale-100 checked:after:bg-primary-default',
        ),
      },
    },
    spacing: {
      sm: { fieldset: cn('gap-sm') },
      md: { fieldset: cn('gap-md') },
      lg: { fieldset: cn('gap-lg') },
      xl: { fieldset: cn('gap-xl') },
    },
  },
  defaultVariants: {
    spacing: 'sm',
  },
});

export type RadioGroupProps = ComponentProps<
  InputHTMLAttributes<HTMLInputElement>
> & {
  options: RadioGroupOption[];
  variant?: RadioGroupVariant;
  /** Spacing between the options. */
  spacing?: RadioGroupSpacing;
};

export const RadioGroup = forwardRef(
  (
    {
      options,
      name,
      dangerouslyOverrideClassName,
      value,
      disabled,
      readOnly,
      variant = 'tag',
      spacing = 'sm',
      ...inputProps
    }: RadioGroupProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    // Tracks the selection when no `value` is supplied, so that the component
    // can be used in both controlled and uncontrolled forms.
    const [uncontrolledValue, setUncontrolledValue] = useState<string>();

    const overrideClassName = getClassNameOverride(
      { dangerouslyOverrideClassName },
      'RadioGroup',
    );

    const { fieldset, wrapper, input } = radioGroup({ variant, spacing });
    const isDisabled = !!(disabled || readOnly);
    const selectedValue =
      value === undefined ? uncontrolledValue : value.toString();

    return (
      <fieldset className={cn(fieldset(), overrideClassName)}>
        <legend className="sr-only">{name}</legend>
        {options.map((option) => {
          const isSelected = selectedValue === option.value;

          const inputEl = (
            <input
              ref={ref}
              type="radio"
              name={name}
              {...inputProps}
              checked={isSelected}
              value={option.value}
              disabled={disabled}
              readOnly={readOnly}
              onChange={(e) => {
                if (disabled || readOnly) {
                  e.preventDefault();
                  e.stopPropagation();

                  return;
                }

                inputProps.onChange?.(e);
                setUncontrolledValue(option.value);
              }}
              className={input()}
            />
          );

          return (
            <label key={option.value}>
              {variant === 'tag' ? (
                <Tag
                  variant={isSelected ? 'active' : 'secondary'}
                  interactive={!isDisabled}
                  disabled={isDisabled}
                >
                  {inputEl}
                  {option.label}
                </Tag>
              ) : (
                <span className={wrapper()}>
                  {inputEl}
                  {option.label}
                </span>
              )}
            </label>
          );
        })}
      </fieldset>
    );
  },
);

RadioGroup.displayName = 'RadioGroup';
